import { useCallback, useEffect, useRef, useState } from 'react';
import { useSignatureService } from '../services/api/signatureService';
import { useSignatureClientService } from '../services/api/signatureClientService';
import { getLocalClientStatus } from '../services/localSignatureClient';
import { listTokens, signBatch, daemonErrorMessage, getAuthorizationStatus } from '../services/signatureDaemonClient';
import { runBatchLoop } from '../services/batchSignatureRunner';
import { isBelowMinimum } from '../utils/version';
import { ENV } from '../config/env';
import { useStopOnSessionExpired } from '../auth/useStopOnSessionExpired';

// Tuning del loop de firma masiva (#59), configurable por env sin recompilar:
//  - CHUNK_SIZE: digests firmados por sesión de token en cada /sign-batch (el daemon firma N por PIN).
//  - SIGN_DELAY_MS: pausa entre chunks (0 = sin pausa).
// El throttle de 5s + chunk=1 previos eran una mitigación de la teoría "el token falla en ráfaga" que
// el fix #98 (raíz server-side, ADR-032) desmintió. Default 50/0; para lotes de 100k/150k es clave no
// firmar de a 1 con pausa (serían días). Ver env.js SIGNATURE_BATCH_CHUNK_SIZE / _SIGN_DELAY_MS.
const CHUNK_SIZE = Math.max(1, Number(ENV.SIGNATURE_BATCH_CHUNK_SIZE) || 50);
const SIGN_DELAY_MS = Math.max(0, Number(ENV.SIGNATURE_BATCH_SIGN_DELAY_MS) || 0);
const POLL_INTERVAL_MS = 2000;

/**
 * Mensaje para un error de una llamada al SERVIDOR Bóveda durante la masiva (createBatch/nextChunk/
 * submitChunk), NO del cliente local de firma. Antes (#98) cualquier error acá caía en
 * daemonErrorMessage → "verificá que el cliente esté en ejecución", engañando al usuario cuando el
 * daemon estaba perfecto (el test de conectividad daba OK) y lo que fallaba era el servidor (p.ej. un
 * timeout de axios en nextChunk por saturación server-side). Los docs ya firmados no se reprocesan.
 */
const serverErrorMessage = (e) => {
  // #103.1: el runner marca el "lote estancado en ensamblado" con kind='batch_stalled' y un mensaje
  // honesto (sin la palabra "timeout"). Mostrarlo tal cual, sin caer en la rama de "tardó demasiado".
  if (e?.kind === 'batch_stalled') return e.message;
  const isTimeout = e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '');
  if (isTimeout) {
    return 'El servidor tardó demasiado en responder. Reintentá en unos segundos; los documentos ya firmados no se reprocesan.';
  }
  const status = e?.response?.status;
  if (status && status >= 500) {
    return 'El servidor tuvo un error procesando el lote. Reintentá en unos segundos; los documentos ya firmados no se reprocesan.';
  }
  return 'No se pudo completar la firma masiva por un error del servidor. Reintentá; los documentos ya firmados no se reprocesan.';
};

/** Intervalo (ms) entre polls del resolving-phase (ingest async). Inyectable para tests. */
const DEFAULT_RESOLVING_POLL_MS = 1500;

/**
 * Máximo de polls del resolving-phase antes de abortar (stuck-INGESTING). 120 polls × 1500ms = 3 min.
 * Inyectable para tests mediante `_resolvingMaxPolls`.
 */
const MAX_RESOLVING_POLLS = 120;

/**
 * Orquesta el flujo de firma masiva (F1D-5): daemon check → PIN → certs → loop de chunks → poll de
 * ensamblado → done. El PIN vive solo en memoria (pinRef); nunca se persiste ni loguea (ADR-031).
 *
 * @param {{onDone?:Function, _resolvingPollMs?:number, _resolvingMaxPolls?:number}} [opts]
 *   `_resolvingPollMs`: intervalo (ms) entre polls del resolving-phase. Por defecto 1500. Pasar 0 en tests.
 *   `_resolvingMaxPolls`: máximo de iteraciones del resolving-poll antes de abortar. Por defecto 120. Pasar 3 en tests.
 */
export const useBatchSignature = ({ onDone, _resolvingPollMs = DEFAULT_RESOLVING_POLL_MS, _resolvingMaxPolls = MAX_RESOLVING_POLLS } = {}) => {
  const signatureService = useSignatureService();
  const signatureClientService = useSignatureClientService();

  const [state, setState] = useState('idle');
  const [message, setMessage] = useState('');
  const [pin, setPinState] = useState('');
  const [certs, setCerts] = useState([]);
  const [certAlias, setCertAlias] = useState('0');
  // signed = docs firmados+enviados por el cliente (avanza en la fase 'signing', por chunk);
  // assembled/failed = resultado del ensamblado async server-side (avanza en la fase 'polling').
  const [progress, setProgress] = useState({ total: 0, signed: 0, assembled: 0, failed: 0, sellados: 0 });
  const [failures, setFailures] = useState([]);
  const [failuresBreakdown, setFailuresBreakdown] = useState([]);
  // #103.2 Task E': fase operativa que reporta el server (delivering|preparing|assembling|
  // waiting_lease|ingesting, o null). La alimentan las dos fuentes que hablan con el server: el
  // runner (nextChunk, durante la firma) y el poll de ensamblado (getBatchStatus). El modal la usa
  // para decir QUÉ está haciendo el server en vez de un "Procesando…" que no significa nada.
  const [phase, setPhase] = useState(null);

  const pinRef = useRef('');
  const selectionRef = useRef(null); // { docIds?, filter?, count }
  const batchIdRef = useRef(null);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);
  const cancelledRef = useRef(false);
  const resolvingTimerRef = useRef(null);

  const setPin = useCallback((v) => { pinRef.current = v; setPinState(v); }, []);

  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    if (resolvingTimerRef.current) { clearTimeout(resolvingTimerRef.current); resolvingTimerRef.current = null; }
    pinRef.current = '';
    stopPoll();
    setPinState('');
    setCerts([]);
    setPhase(null);   // #103.2 Task E': la fase es del lote en curso; al cancelar deja de aplicar.
    setState('idle');
  }, [stopPoll]);

  // El poll del lote es el que mas ruido hace: con la sesion muerta sigue pidiendo el estado cada
  // pocos segundos y la API le responde 401 a todo. En el incidente que origino #168 fueron 9414
  // requests en 70 minutos, unas 2,2 por segundo — eso es un setInterval, no alguien clickeando.
  //
  // Se engancha `cancel` y NO `stopPoll`: `stopPoll` solo mata el setInterval, y como ese
  // intervalo es el cuerpo de la promesa de `pollUntilDone`, matarlo sin resolver dejaba el lote
  // en "Procesando..." para siempre. Peor: `runBatchLoop` es un loop de chunks independiente que
  // solo mira `cancelledRef`, asi que seguia firmando y submitteando de verdad. En una boveda
  // documental, no saber que quedo firmado es el peor lugar donde estar.
  useStopOnSessionExpired(cancel);

  // CRÍTICO: setear mountedRef=true en el mount, no sólo false en el cleanup. Bajo React.StrictMode
  // (dev) React hace mount → cleanup → re-mount del efecto; si el body no re-setea true, el cleanup
  // deja mountedRef=false PERMANENTE → todos los `if (mountedRef.current) setProgress/setState`
  // quedan bloqueados (barra sin avance, nunca llega a 'done'). Con el set en el body, termina true.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPoll();
      pinRef.current = '';
      if (resolvingTimerRef.current) { clearTimeout(resolvingTimerRef.current); resolvingTimerRef.current = null; }
    };
  }, [stopPoll]);

  /**
   * ¿El cliente instalado puede cumplir el contrato de firma actual? (#108)
   *
   * Un cliente por debajo del mínimo ignora el nivel PAdES y firma todo sin sello: esas firmas se
   * rechazan al publicarlas, con el PIN ya ingresado y una operación del token gastada por
   * documento. Se corta antes de pedir el PIN.
   *
   * Best-effort: si el manifest no responde o no declara mínimo, se sigue. No se bloquea a nadie
   * por datos que no se pudieron obtener — el control del lado del servidor sigue en pie.
   */
  const clientePorDebajoDelMinimo = useCallback(async (versionInstalada) => {
    try {
      const manifest = await signatureClientService.getManifest();
      return isBelowMinimum(versionInstalada, manifest?.minimumVersion);
    } catch {
      return false;
    }
  }, [signatureClientService]);

  // Gate de consentimiento por origen (ADR-033): verifica /authorize antes de pedir el PIN.
  // Retorna true si el origen está autorizado y el caller debe pasar a 'pin'.
  // Retorna false si fijó un estado terminal (awaiting_consent con poll, consent_timeout, o
  // daemon_down) — el caller debe salir sin continuar.
  // El poll corre cada 2s hasta 60s; respeta mountedRef para no actualizar tras desmontaje.
  const ensureAuthorizedOrigin = useCallback(async () => {
    let auth;
    try {
      auth = await getAuthorizationStatus();
    } catch {
      setState('daemon_down');
      return false;
    }
    if (auth.authorized) return true;
    setState('awaiting_consent');
    const started = Date.now();
    const poll = async () => {
      if (!mountedRef.current) return;
      let a;
      try {
        a = await getAuthorizationStatus();
      } catch {
        if (mountedRef.current) setState('daemon_down');
        return;
      }
      if (!mountedRef.current) return;
      if (a.authorized) { setState('pin'); return; }
      if (Date.now() - started > 60000) { setState('consent_timeout'); return; }
      setTimeout(poll, 2000);
    };
    setTimeout(poll, 2000);
    return false;
  }, []);

  // 1) Iniciar un lote nuevo desde una selección: chequear daemon → consentimiento → PIN.
  const begin = useCallback(async ({ docIds, filter, count }) => {
    cancelledRef.current = false;
    selectionRef.current = { docIds, filter, count };
    batchIdRef.current = null;
    setFailures([]);
    setFailuresBreakdown([]);
    setProgress({ total: count || 0, signed: 0, assembled: 0, failed: 0, sellados: 0 });
    setMessage('');
    setState('checking');
    const status = await getLocalClientStatus();
    if (!status) { setState('daemon_down'); return; }
    if (await clientePorDebajoDelMinimo(status.version)) { setState('client_outdated'); return; }
    const authorized = await ensureAuthorizedOrigin();
    if (!authorized) return;
    setState('pin');
  }, [ensureAuthorizedOrigin, clientePorDebajoDelMinimo]);

  // 1b) Reanudar un lote existente: chequear daemon → consentimiento → PIN (igual que begin).
  // ADR-033: el gate de consentimiento aplica también al resume; sin él, el origen podría
  // mostrar el PIN en un flujo de reanudación sin haber obtenido la autorización nativa.
  const resume = useCallback(async ({ batchId, count, retryFailed = true }) => {
    cancelledRef.current = false;
    selectionRef.current = { count, retryFailed };
    batchIdRef.current = batchId;
    setFailures([]);
    setFailuresBreakdown([]);
    setProgress({ total: count || 0, signed: 0, assembled: 0, failed: 0, sellados: 0 });
    setMessage('');
    setState('checking');
    const status = await getLocalClientStatus();
    if (!status) { setState('daemon_down'); return; }
    if (await clientePorDebajoDelMinimo(status.version)) { setState('client_outdated'); return; }
    const authorized = await ensureAuthorizedOrigin();
    if (!authorized) return;
    setState('pin');
  }, [ensureAuthorizedOrigin, clientePorDebajoDelMinimo]);

  // 2) PIN → certs del token.
  const loadCerts = useCallback(async () => {
    if (!pinRef.current) return;
    setState('signing');
    setMessage('');
    let tokens;
    try {
      tokens = await listTokens({ pin: pinRef.current });
    } catch (e) {
      setMessage(daemonErrorMessage(e));
      setState('pin');
      return;
    }
    if (!tokens.length) { setMessage('El token no tiene certificados disponibles.'); setState('pin'); return; }
    setCerts(tokens);
    setCertAlias(tokens[0].alias);
    setState('certs');
  }, []);

  const pollUntilDone = useCallback((batchId) => new Promise((resolve) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await signatureService.getBatchStatus(batchId);
        const d = res?.data || {};
        const total = d.totalDocuments || 0;
        const assembled = d.assembled || 0;
        const inFlight = d.signed || 0; // docs SIGNED esperando ensamblado async
        if (mountedRef.current) setProgress((p) => ({ ...p, total, assembled, failed: d.failed || 0 }));
        // #103.2 Task E': fase real del server durante el ensamblado (típicamente 'assembling').
        if (mountedRef.current) setPhase(d.phase ?? null);
        // Reflejar el breakdown tal cual lo reporta el server (incl. vacío) para no dejar fallos
        // colgados si un reintento los limpió; el server es la fuente de verdad del estado del lote.
        if (mountedRef.current) setFailuresBreakdown(d.failedDocs || []);
        // El loop de chunks ya corrió ENTERO antes de este poll: no entran nuevos SIGNED, sólo
        // drenan. Completar cuando el pipeline async drenó (inFlight === 0). Lo que quede en
        // PENDING/PREPARED son docs que el cliente no pudo firmar esta pasada (daemon caído o CMS
        // rechazado en submit, que queda PREPARED, ver SignatureService.submitChunk): NO cuentan en
        // assembled ni failed, así que la condición vieja (assembled+failed>=total) nunca se cumplía
        // y el modal quedaba colgado en "Procesando…".
        // CONTADOR AUTORITATIVO (#103, review final): usar d.failed del server (conteo por estado DB)
        // en lugar de (total - assembled) — los docs PENDING/PREPARED atascados no son FAILED en DB
        // y la derivación producía un número inconsistente con el banner de reanudación.
        if (total > 0 && inFlight === 0) {
          stopPoll();
          if (mountedRef.current) setProgress((p) => ({ ...p, total, assembled, failed: d.failed || 0 }));
          resolve();
        }
      } catch { /* transitorio: seguir poleando */ }
    }, POLL_INTERVAL_MS);
  }), [signatureService, stopPoll]);

  // 3) Crear el lote (si es nuevo) y correr el loop + poll.
  const run = useCallback(async () => {
    cancelledRef.current = false;
    setState('signing');
    setMessage('');
    try {
      if (!batchIdRef.current) {
        const sel = selectionRef.current || {};
        // createBatch ahora retorna 202 { batchId, taskId } sin totalDocuments (#103, ADR-036).
        // El ingest corre en background; esperar a que el lote esté listo antes de firmar.
        const res = await signatureService.createBatch({ docIds: sel.docIds, filter: sel.filter });
        batchIdRef.current = res?.data?.batchId;

        // Fase "resolving": pollear getBatchStatus hasta que el ingest termine (COMPLETED)
        // o haya al menos un digest preparado (prepared>0) — lo que ocurra primero.
        // Actualizar progress.total a medida que se descubre totalDocuments.
        // Termina también si: FAILED (error), CANCELLED server-side (stop limpio), user cancel,
        // o se supera _resolvingMaxPolls (timeout para lotes stuck en INGESTING).
        if (mountedRef.current) setState('resolving');
        let resolvingPollCount = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // Chequear cancel al inicio de cada iteración.
          if (cancelledRef.current) return;

          const statusRes = await signatureService.getBatchStatus(batchIdRef.current);
          const s = statusRes?.data || {};
          const ingestState = s.ingestState;
          const totalDocs = s.totalDocuments || 0;
          const prepared = s.prepared || 0;

          if (totalDocs > 0 && mountedRef.current) {
            setProgress((p) => ({ ...p, total: totalDocs }));
          }
          // #103.2 Task E': durante el resolving la fase suele ser 'ingesting' — mostrarla es más
          // honesto que el genérico "Resolviendo…" cuando el ingest de un lote grande tarda.
          if (mountedRef.current) setPhase(s.phase ?? null);

          if (ingestState === 'FAILED') {
            const err = new Error('La ingesta del lote falló. No se puede firmar. Cancelá el lote y volvé a intentarlo.');
            err.source = 'ingest';
            throw err;
          }

          // Cancel server-side: stop limpio, no firmar.
          if (s.state === 'CANCELLED') return;

          if (ingestState === 'COMPLETED' || prepared > 0) {
            break;
          }

          resolvingPollCount += 1;
          if (resolvingPollCount >= _resolvingMaxPolls) {
            const err = new Error('El lote no terminó de resolverse a tiempo. Reintentá más tarde.');
            err.source = 'ingest';
            throw err;
          }

          // El ingest sigue corriendo: esperar antes del próximo poll.
          // _resolvingPollMs=0 en tests para poll instantáneo.
          if (_resolvingPollMs > 0) {
            await new Promise((resolve) => {
              resolvingTimerRef.current = setTimeout(resolve, _resolvingPollMs);
            });
            resolvingTimerRef.current = null;
          }
          if (!mountedRef.current || cancelledRef.current) return;
        }

        // Verificar cancel justo antes de entrar al loop de firma.
        if (cancelledRef.current) return;

        if (mountedRef.current) setState('signing');
      }
      // Adaptador: el servicio devuelve respuestas axios ({ data: {...} }), pero el runner
      // trabaja con la forma plana de la API (#103, ADR-036). Desenvuelve .data antes de
      // pasar al loop, manteniendo el runner agnóstico de axios.
      const apiAdapter = {
        nextChunk: async (...args) => {
          const res = await signatureService.nextChunk(...args);
          return res?.data ?? res;
        },
        submitChunk: (...args) => signatureService.submitChunk(...args),
      };
      const { failures: loopFailures } = await runBatchLoop({
        batchId: batchIdRef.current,
        pin: pinRef.current,
        certAlias,
        chunkSize: CHUNK_SIZE,
        chunkDelayMs: SIGN_DELAY_MS,
        api: apiAdapter,
        daemonSignBatch: signBatch,
        retryFailed: selectionRef.current?.retryFailed ?? false,
        // Feedback en la fase de firma: acumular los docs firmados+enviados por chunk. Sin esto la
        // barra quedaba congelada en 0/N durante toda la firma (sólo el poll de ensamblado la movía).
        onProgress: ({ signedThisChunk = 0, phase: reportedPhase, selladosEnChunk = 0 } = {}) => {
          if (mountedRef.current) {
            setProgress((p) => ({
              ...p,
              signed: (p.signed || 0) + signedThisChunk,
              // #108: documentos con sello de tiempo vistos hasta acá. Alimenta el aviso de costo:
              // cada uno cuesta ~15 s contra la TSA, en la máquina del usuario.
              sellados: (p.sellados || 0) + selladosEnChunk,
            }));
            // #103.2 Task E': el runner reporta la fase en cada chunk Y en cada poll de espera —
            // así el modal dice "Preparando…"/"Esperando la entrega…" en vez de quedarse mudo.
            if (reportedPhase !== undefined) setPhase(reportedPhase);
          }
        },
      });
      setFailures(loopFailures);
      setState('polling');
      await pollUntilDone(batchIdRef.current);
      if (!mountedRef.current) return;
      pinRef.current = '';
      setState('done');
      onDone?.();
    } catch (e) {
      stopPoll();
      // #98: distinguir el origen. Errores del daemon (source='daemon') → mensaje del cliente local.
      // Errores de ingest (source='ingest') → usar el mensaje del error directamente (ya específico).
      // Errores del servidor Bóveda → serverErrorMessage honesto (no culpa al daemon).
      if (e?.source === 'daemon') {
        setMessage(daemonErrorMessage(e));
      } else if (e?.source === 'ingest') {
        setMessage(e.message);
      } else {
        setMessage(serverErrorMessage(e));
      }
      setState('paused');
    }
  }, [signatureService, certAlias, pollUntilDone, onDone, stopPoll, _resolvingPollMs, _resolvingMaxPolls]);

  // Reintentar tras pausa: reanuda el loop con el mismo batchId (nextChunk re-entrega lo no firmado).
  const retry = useCallback(() => run(), [run]);

  // Cancela el lote en el server y limpia el estado local.
  const cancelBatch = useCallback(async () => {
    try {
      if (batchIdRef.current) await signatureService.cancelBatch(batchIdRef.current);
    } finally {
      cancel();
    }
  }, [signatureService, cancel]);

  // Reanudar el lote actual con retryFailed=true (para reintentar los docs PREPARE_ERROR/BLOQUEADO).
  const resumeWithRetry = useCallback(async () => {
    if (!batchIdRef.current) return;
    await resume({ batchId: batchIdRef.current, count: selectionRef.current?.count, retryFailed: true });
  }, [resume]);

  return {
    state, message, pin, setPin, certs, certAlias, setCertAlias, progress, failures,
    failuresBreakdown, phase, begin, resume, loadCerts, run, retry, cancel, cancelBatch, resumeWithRetry,
  };
};

export default useBatchSignature;
